import { normalizePhoneNumber } from "../../utils/phone";

const empty = (value) => {
  if (value === undefined || value === null) return true;
  if (Array.isArray(value)) return !value.length;
  return String(value).trim() === "";
};

const placeholderTitle = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();
  if (!text) return true;
  return /^(title|name|package|new package|باقة|عنوان)(\s*الجديدة)?$/.test(text);
};

const packageTitle = (fields, language) => {
  const arabic = language === "Arabic";
  const kinds = arabic
    ? { class: "كلاس", GYM: "اشتراك", pt: "موعد", court: "ملعب" }
    : { class: "Class", GYM: "Gym", pt: "Private", court: "Court" };
  const kind = kinds[fields.memberShipType] || (arabic ? "باقة" : "Package");
  const parts = [kind];
  const months = Number(fields.days) || 1;
  if (fields.duration === "M") {
    parts.push(
      arabic
        ? months === 1
          ? "شهري"
          : `${months} أشهر`
        : months === 1
        ? "monthly"
        : `${months} months`
    );
  } else if (!empty(fields.days)) {
    parts.push(arabic ? `${fields.days} يوم` : `${fields.days} days`);
  }
  if (!empty(fields.session) && String(fields.session) !== "unlimited") {
    parts.push(arabic ? `${fields.session} حصص` : `${fields.session} sessions`);
  }
  return parts.join(" — ");
};

const draftName = (draft) => {
  const fields = (draft && draft.fields) || {};
  if (draft.type === "member") {
    return (
      [fields.firstName, fields.lastName].filter(Boolean).join(" ") ||
      fields.name ||
      ""
    );
  }
  return fields.title || fields.name || "";
};

const hasLinkedRecord = (value) => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return list.some((item) => item && (item.id || item.pending));
};

const spokenWeeklyDays = (text) => {
  const match = String(text || "").match(
    /(\d+)\s*(?:days?\s*(?:a|per)\s*week|أيام?\s*(?:بال|في\s*)?أ?سبوع)/i
  );
  return match ? Number(match[1]) : null;
};

const spokenPrice = (text) => {
  const match = String(text || "").match(
    /(\d+(?:\.\d+)?)\s*(?:JOD|JD|دنانير|دينار|د\.?\s*أ)|(?:بـ|بسعر|سعر(?:ها|ه)?)\s*(\d+(?:\.\d+)?)|(\d+(?:\.\d+)?)\s*(?:\/|لكل|per)\s*(?:one\s+)?(?:appointment|موعد|حصة)/i
  );
  if (!match) return null;
  return Number(match[1] || match[2] || match[3]);
};

const appointmentName = (language) =>
  language === "Arabic" ? "موعد" : "Appointment";

const packageLink = (pack) => {
  const session = pack.fields && pack.fields.session;
  const count =
    session && String(session) !== "unlimited" && Number(session) > 0
      ? Number(session)
      : 1;
  return {
    pending: pack.id,
    name: (pack.fields && pack.fields.title) || "",
    count,
  };
};

const ensureSale = (drafts) => {
  if (drafts.some((draft) => draft.type === "sale")) return drafts;
  const members = drafts.filter((draft) => draft.type === "member");
  const packs = drafts.filter((draft) => draft.type === "package");
  const products = drafts.filter((draft) => draft.type === "product");
  if (!members.length || (!packs.length && !products.length)) return drafts;
  const member = members[0];
  const name = draftName(member);
  return drafts.concat([
    {
      id: `sale-${member.id || "member"}`,
      type: "sale",
      label: "Add Sale",
      fields: {
        member: { pending: member.id, name },
        ...(packs.length
          ? {
              packages: packs.map((pack) => ({
                pending: pack.id,
                name: draftName(pack),
              })),
            }
          : {}),
        ...(products.length
          ? {
              products: products.map((item) => ({
                pending: item.id,
                name: draftName(item),
              })),
            }
          : {}),
      },
      missing: [],
      saved: [
        { field: "Member", value: name },
        {
          field: "Package or product",
          value: draftName(packs[0] || products[0]),
        },
      ],
      fieldErrors: [],
      ready: true,
    },
  ]);
};

const ensurePtAppointment = (drafts, language) => {
  const packs = drafts.filter(
    (draft) =>
      draft.type === "package" &&
      String((draft.fields || {}).memberShipType) === "pt"
  );
  if (!packs.length) return drafts;
  if (drafts.some((draft) => draft.type === "appointment")) {
    return drafts.map((draft) => {
      if (draft.type !== "appointment") return draft;
      const fields = { ...(draft.fields || {}) };
      if (!hasLinkedRecord(fields.packages)) {
        fields.packages = packs.map(packageLink);
      }
      if (empty(fields.name)) {
        fields.name =
          (packs[0].fields && packs[0].fields.title) ||
          appointmentName(language);
      }
      if (empty(fields.duration)) fields.duration = 60;
      return dropMissing(
        { ...draft, fields },
        ["Name", "Packages", "Assign To", "Duration", "Length in minutes"],
        { field: "Name", value: fields.name }
      );
    });
  }
  return drafts.concat(
    packs.map((pack) => {
      const name =
        (pack.fields && pack.fields.title) || appointmentName(language);
      return {
        id: `apt-${pack.id || "pt"}`,
        type: "appointment",
        label: "Add Appointment",
        fields: {
          name,
          duration: 60,
          packages: [packageLink(pack)],
        },
        missing: ["Trainers"],
        saved: [
          { field: "Name", value: name },
          { field: "Length in minutes", value: "60" },
          { field: "Packages", value: name },
        ],
        fieldErrors: [],
        ready: false,
      };
    })
  );
};

const dropMissing = (draft, labels, savedItem) => {
  const missing = (draft.missing || []).filter(
    (item) => labels.indexOf(item) === -1
  );
  const saved = (draft.saved || []).filter(
    (item) => labels.indexOf(item.field) === -1
  );
  if (savedItem && savedItem.value) saved.push(savedItem);
  return {
    ...draft,
    missing,
    saved,
    ready: missing.length === 0 && !(draft.fieldErrors || []).length,
  };
};

const fillMemberPhone = (draft, country) => {
  if (draft.type !== "member" || empty(draft.fields && draft.fields.phone)) {
    return draft;
  }
  const phone = normalizePhoneNumber(draft.fields.phone, country);
  if (!phone.valid) return draft;
  const fields = { ...draft.fields, phone: phone.value };
  const fieldErrors = (draft.fieldErrors || []).filter(
    (item) => item && item.key !== "phone" && item.field !== "phone"
  );
  return dropMissing(
    { ...draft, fields, fieldErrors },
    ["Phone", "Valid Phone", "Email or Phone"],
    { field: "Phone", value: phone.value }
  );
};

export const applyClientVoiceDefaults = (drafts, context = {}) => {
  if (!Array.isArray(drafts)) return [];
  const language = context.language;
  const price = spokenPrice(context.message);
  const withTitles = drafts.map((draft) => {
    if (draft.type !== "package") return draft;
    const fields = { ...(draft.fields || {}) };
    const labels = [];
    const extras = [];
    const weeklyDays = spokenWeeklyDays(context.message);
    if (price !== null && (empty(fields.price) || weeklyDays)) {
      fields.price = price;
      labels.push("Price");
      extras.push({ field: "Price", value: String(price) });
    }
    if (weeklyDays) {
      fields.session = "unlimited";
      labels.push("Session");
      extras.push({ field: "Session", value: "unlimited" });
    }
    if (String(fields.memberShipType) === "pt") {
      const capacity = Number(fields.capacity);
      if (
        empty(fields.capacity) ||
        !Number.isFinite(capacity) ||
        capacity <= 1 ||
        (weeklyDays && capacity === weeklyDays)
      ) {
        fields.capacity = 1;
      }
      if (empty(fields.pricingType)) fields.pricingType = "static";
    }
    if (placeholderTitle(fields.title)) {
      fields.title = packageTitle(fields, language);
      labels.push("Title", "Name");
      extras.push({ field: "Title", value: fields.title });
    }
    if (!labels.length) return { ...draft, fields };
    return extras.reduce(
      (next, item) => dropMissing(next, labels, item),
      { ...draft, fields }
    );
  });
  const withSales = withTitles.map((draft) => {
    if (draft.type !== "sale") return draft;
    const fields = { ...(draft.fields || {}) };
    if (hasLinkedRecord(fields.packages) || hasLinkedRecord(fields.products)) {
      return draft;
    }
    const packages = withTitles.filter((item) => item.type === "package");
    if (packages.length !== 1) return draft;
    const pack = packages[0];
    const name = (pack.fields && pack.fields.title) || "";
    fields.packages = [{ pending: pack.id, name }];
    return dropMissing(
      { ...draft, fields },
      ["Package or product", "Packages"],
      { field: "Package or product", value: name }
    );
  });
  return ensurePtAppointment(ensureSale(withSales), language).map((draft) =>
    fillMemberPhone(draft, context.country)
  );
};
